import clsx from 'clsx'
import {useMemo} from 'react'

type Size = 'tiny' | 'small' | 'medium' | 'large' | 'huge'

export interface IconProps {
	className?: string
	size?: Size
	direction?: 'left' | 'right' | 'top' | 'bottom'
	withNotification?: boolean
	strokeWidth?: number
}

function computeClassName(props: IconProps): string {
	return clsx(getSize(props.size ?? 'medium'), props.className)
}

function getSize(size: Size): string {
	switch (size) {
		case 'tiny': {
			return 'w-4 h-4'
		}

		case 'small': {
			return 'w-6 h-6'
		}

		case 'medium': {
			return 'w-9 h-9'
		}

		case 'large': {
			return 'w-12 h-12'
		}

		case 'huge': {
			return 'w-20 h-20'
		}
	}
}

export function MenuIcon(props: IconProps) {
	return (
		<svg
			data-testid="menuIcon"
			className={computeClassName(props)}
			viewBox="0 0 24 24"
			strokeWidth={props.strokeWidth ?? 1.5}
			stroke="currentColor"
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path stroke="none" d="M0 0h24v24H0z" fill="none" />
			<line x1="4" y1="6" x2="20" y2="6" />
			<line x1="4" y1="12" x2="20" y2="12" />
			<line x1="4" y1="18" x2="20" y2="18" />
		</svg>
	)
}

export function Logo(props: IconProps) {
	return (
		<svg
			data-testid="logo"
			className={computeClassName(props)}
			width="114"
			height="125"
			viewBox="0 0 114 125"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M41.2737 33.1981C26.5181 25.4169 29.473 10.5801 32.7949 4.13436C43.4566 10.0531 46.169 22.0707 43.0593 33.6765L41.2737 33.1981Z"
				fill="#8BC53F"
			/>
			<path
				d="M40.2669 34.8074C16.4972 42.1634 3.96532 23.3985 0.670562 13.0965C18.1581 8.09401 33.6939 17.9083 42.6547 33.4288L40.2669 34.8074Z"
				fill="#4EA32A"
			/>
			<path
				d="M110.919 59.8844C113.052 59.3127 114.335 57.111 113.579 55.0353C110.375 46.2404 104.542 38.6388 96.8764 33.2677C95.0671 32 92.6084 32.6695 91.5038 34.5826L75.4977 62.3061C73.7158 65.3923 76.5548 69.0922 79.997 68.1698L110.919 59.8844Z"
				fill="#FFCE27"
			/>
			<path
				d="M88.5038 32.5826C89.6084 30.6694 88.9588 28.2055 86.9563 27.2724C78.4718 23.319 68.9722 22.0684 59.7536 23.6911C57.5779 24.0741 56.3127 26.286 56.8845 28.4198L65.1698 59.3414C66.0922 62.7836 70.7158 63.3923 72.4977 60.3061L88.5038 32.5826Z"
				fill="#FFCE27"
			/>
			<path
				d="M52.8844 29.4198C52.3127 27.286 50.111 26.003 48.0353 26.7592C39.2404 29.9632 31.6388 35.7961 26.2677 43.4621C25.0001 45.2713 25.6695 47.73 27.5826 48.8346L55.3061 64.8408C58.3923 66.6226 62.0922 63.7836 61.1698 60.3414L52.8844 29.4198Z"
				fill="#FFCE27"
			/>
			<path
				d="M25.5826 51.8346C23.6694 50.73 21.2055 51.3797 20.2724 53.3821C16.319 61.8666 15.0684 71.3662 16.6911 80.5848C17.0741 82.7605 19.286 84.0257 21.4198 83.454L52.3414 75.1686C55.7836 74.2463 56.3923 69.6226 53.3061 67.8408L25.5826 51.8346Z"
				fill="#FFCE27"
			/>
			<path
				d="M22.4198 87.454C20.286 88.0258 19.003 90.2274 19.7592 92.3031C22.9632 101.098 28.7961 108.7 36.4621 114.071C38.2713 115.338 40.73 114.669 41.8346 112.756L57.8408 85.0323C59.6226 81.9461 56.7836 78.2463 53.3414 79.1686L22.4198 87.454Z"
				fill="#FFCE27"
			/>
			<path
				d="M44.8346 114.756C43.73 116.669 44.3797 119.133 46.3821 120.066C54.8666 124.019 64.3662 125.27 73.5848 123.647C75.7605 123.264 77.0257 121.052 76.454 118.919L68.1686 87.997C67.2463 84.5548 62.6226 83.9461 60.8408 87.0323L44.8346 114.756Z"
				fill="#FFCE27"
			/>
			<path
				d="M80.454 118.919C81.0258 121.052 83.2274 122.335 85.3031 121.579C94.0981 118.375 101.7 112.542 107.071 104.876C108.338 103.067 107.669 100.608 105.756 99.5039L78.0323 83.4977C74.9461 81.7158 71.2463 84.5548 72.1686 87.997L80.454 118.919Z"
				fill="#FFCE27"
			/>
		</svg>
	)
}

export function Logo2(props: IconProps) {
	return (
		<svg
			data-testid="logo"
			className={computeClassName(props)}
			width="100"
			height="100"
			viewBox="0 0 100 100"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<circle cx="50" cy="50" r="50" fill="url(#paint0_linear_630_879)" />
			<path
				d="M70.7968 61.7282C53.05 52.3697 56.6039 34.5253 60.5992 26.7729C73.4222 33.8914 76.6844 48.3452 72.9442 62.3036L70.7968 61.7282Z"
				fill="#8BC53F"
			/>
			<path
				d="M68.4294 63.8634C39.8414 72.7105 24.7691 50.1417 20.8065 37.7514C41.8389 31.7349 60.524 43.5386 71.3012 62.2053L68.4294 63.8634Z"
				fill="#4EA32A"
			/>
			<defs>
				<linearGradient
					id="paint0_linear_630_879"
					x1="50"
					y1="0"
					x2="50"
					y2="100"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#FFCE27" />
					<stop offset="1" stopColor="#FFEA0B" />
				</linearGradient>
			</defs>
		</svg>
	)
}

export function ChevronIcon(props: IconProps) {
	const directionClass = useMemo(() => {
		switch (props.direction) {
			case 'left':
				return 'transform -rotate-180'
			case 'top':
				return 'transform -rotate-90'
			case 'bottom':
				return 'transform rotate-90'
			case 'right':
			default:
				return ''
		}
	}, [props.direction])
	return (
		<svg
			data-testid="chevronIcon"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			className={clsx(computeClassName(props), directionClass)}
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={props.strokeWidth ?? 1.5}
				d="M9 5l7 7-7 7"
			/>
		</svg>
	)
}

export function LoadingSpinner(props: IconProps) {
	return (
		<div
			data-testid="loadingSpinner"
			className={clsx(
				'spinner-border animate-spin inline-block',
				computeClassName(props)
			)}
			role="status"
			style={{
				border: '3px solid rgba(251, 191, 36, .3)',
				borderRadius: '50%',
				borderTopColor: 'rgb(251, 191, 36)',
				animation: 'spin 1s ease-in-out infinite',
			}}
		/>
	)
}

export function ArrowsSortIcon(props: IconProps) {
	return (
		<svg
			data-testid="arrowsSortIcon"
			className={computeClassName(props)}
			viewBox="0 0 24 24"
			strokeWidth={props.strokeWidth ?? 1.5}
			stroke="currentColor"
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path stroke="none" d="M0 0h24v24H0z" fill="none" />
			<path d="M3 9l4 -4l4 4m-4 -4v14" />
			<path d="M21 15l-4 4l-4 -4m4 4v-14" />
		</svg>
	)
}

export function CheckIcon(props: IconProps) {
	return (
		<svg
			data-testid="checkIcon"
			className={computeClassName(props)}
			viewBox="0 0 24 24"
			strokeWidth={props.strokeWidth ?? 1.5}
			stroke="currentColor"
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path stroke="none" d="M0 0h24v24H0z" fill="none" />
			<path d="M5 12l5 5l10 -10" />
		</svg>
	)
}

export function XIcon(props: IconProps) {
	return (
		<svg
			data-testid="xIcon"
			className={computeClassName(props)}
			viewBox="0 0 24 24"
			strokeWidth={props.strokeWidth ?? 1.5}
			stroke="currentColor"
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path stroke="none" d="M0 0h24v24H0z" fill="none" />
			<line x1="18" y1="6" x2="6" y2="18" />
			<line x1="6" y1="6" x2="18" y2="18" />
		</svg>
	)
}

export function MetaMaskLogo({className}: {className?: string}) {
	return (
		<svg
			className={className}
			width="170"
			height="33"
			viewBox="0 0 170 33"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M150.294 16.5007C149.417 15.9138 148.45 15.4965 147.534 14.9748C146.941 14.6357 146.308 14.3356 145.793 13.9052C144.915 13.1749 145.096 11.7402 146.012 11.1141C147.328 10.2273 149.507 10.7228 149.739 12.5358C149.739 12.5748 149.778 12.601 149.817 12.601H151.803C151.855 12.601 151.894 12.5619 151.881 12.5097C151.778 11.2576 151.3 10.2142 150.423 9.549C149.585 8.90991 148.63 8.5708 147.611 8.5708C142.362 8.5708 141.884 14.1922 144.709 15.966C145.032 16.1747 147.804 17.5832 148.785 18.1962C149.765 18.8092 150.075 19.9309 149.649 20.8178C149.262 21.6265 148.256 22.1873 147.25 22.1221C146.153 22.0569 145.303 21.457 145.006 20.5179C144.954 20.3483 144.928 20.0222 144.928 19.8788C144.928 19.8396 144.89 19.8005 144.851 19.8005H142.698C142.659 19.8005 142.62 19.8396 142.62 19.8788C142.62 21.457 143.007 22.3308 144.065 23.1264C145.057 23.8829 146.141 24.1959 147.263 24.1959C150.204 24.1959 151.726 22.5133 152.035 20.7657C152.306 19.057 151.803 17.518 150.294 16.5007Z"
				fill="white"
			/>
			<path
				d="M56.7747 8.85785H55.8204H54.7756C54.7369 8.85785 54.7111 8.88393 54.6982 8.91002L52.9313 14.8053C52.9055 14.8836 52.8024 14.8836 52.7766 14.8053L51.0096 8.91002C50.9967 8.87088 50.9709 8.85785 50.9322 8.85785H49.8876H48.9331H47.6434C47.6046 8.85785 47.5659 8.89697 47.5659 8.9361V23.9874C47.5659 24.0264 47.6046 24.0656 47.6434 24.0656H49.7972C49.8359 24.0656 49.8746 24.0264 49.8746 23.9874V12.5489C49.8746 12.4576 50.0036 12.4446 50.0294 12.5228L51.8092 18.4573L51.9382 18.8746C51.9511 18.9137 51.9769 18.9268 52.0156 18.9268H53.6665C53.7051 18.9268 53.7309 18.9006 53.7438 18.8746L53.8728 18.4573L55.6527 12.5228C55.6785 12.4315 55.8074 12.4576 55.8074 12.5489V23.9874C55.8074 24.0264 55.8462 24.0656 55.8848 24.0656H58.0387C58.0774 24.0656 58.1161 24.0264 58.1161 23.9874V8.9361C58.1161 8.89697 58.0774 8.85785 58.0387 8.85785H56.7747Z"
				fill="white"
			/>
			<path
				d="M117.315 8.85785C117.276 8.85785 117.25 8.88393 117.237 8.91002L115.471 14.8053C115.445 14.8836 115.342 14.8836 115.316 14.8053L113.549 8.91002C113.536 8.87088 113.511 8.85785 113.472 8.85785H110.196C110.157 8.85785 110.118 8.89697 110.118 8.9361V23.9874C110.118 24.0264 110.157 24.0656 110.196 24.0656H112.35C112.389 24.0656 112.427 24.0264 112.427 23.9874V12.5489C112.427 12.4576 112.556 12.4446 112.582 12.5228L114.361 18.4573L114.49 18.8746C114.503 18.9137 114.529 18.9268 114.568 18.9268H116.219C116.258 18.9268 116.283 18.9006 116.296 18.8746L116.425 18.4573L118.205 12.5228C118.231 12.4315 118.36 12.4576 118.36 12.5489V23.9874C118.36 24.0264 118.399 24.0656 118.438 24.0656H120.591C120.63 24.0656 120.669 24.0264 120.669 23.9874V8.9361C120.669 8.89697 120.63 8.85785 120.591 8.85785H117.315Z"
				fill="white"
			/>
			<path
				d="M89.5343 8.85785H85.5231H83.3693H79.3582C79.3195 8.85785 79.2808 8.89697 79.2808 8.9361V10.8142C79.2808 10.8534 79.3195 10.8925 79.3582 10.8925H83.2919V23.9874C83.2919 24.0264 83.3305 24.0656 83.3693 24.0656H85.5231C85.5618 24.0656 85.6006 24.0264 85.6006 23.9874V10.8925H89.5343C89.573 10.8925 89.6116 10.8534 89.6116 10.8142V8.9361C89.6116 8.89697 89.5859 8.85785 89.5343 8.85785Z"
				fill="white"
			/>
			<path
				d="M102.239 24.0653H104.198C104.25 24.0653 104.289 24.0132 104.276 23.9611L100.226 8.85765C100.213 8.81853 100.187 8.80548 100.149 8.80548H99.4008H98.0853H97.3372C97.2985 8.80548 97.2727 8.83157 97.2598 8.85765L93.21 23.9611C93.1971 24.0132 93.2358 24.0653 93.2875 24.0653H95.2478C95.2865 24.0653 95.3123 24.0393 95.3253 24.0132L96.4989 19.6178C96.5117 19.5787 96.5375 19.5657 96.5763 19.5657H100.91C100.949 19.5657 100.975 19.5917 100.988 19.6178L102.161 24.0132C102.174 24.0393 102.213 24.0653 102.239 24.0653ZM97.0921 17.4136L98.6657 11.5314C98.6914 11.4531 98.7946 11.4531 98.8205 11.5314L100.394 17.4136C100.407 17.4658 100.368 17.5179 100.316 17.5179H97.1696C97.1179 17.5179 97.0793 17.4658 97.0921 17.4136Z"
				fill="white"
			/>
			<path
				d="M135.669 24.0653H137.628C137.68 24.0653 137.719 24.0132 137.706 23.9611L133.656 8.85765C133.643 8.81853 133.618 8.80548 133.579 8.80548H132.831H131.516H130.767C130.729 8.80548 130.703 8.83157 130.69 8.85765L126.64 23.9611C126.627 24.0132 126.666 24.0653 126.718 24.0653H128.678C128.717 24.0653 128.743 24.0393 128.756 24.0132L129.929 19.6178C129.942 19.5787 129.968 19.5657 130.007 19.5657H134.34C134.379 19.5657 134.405 19.5917 134.418 19.6178L135.591 24.0132C135.604 24.0393 135.63 24.0653 135.669 24.0653ZM130.522 17.4136L132.096 11.5314C132.121 11.4531 132.225 11.4531 132.25 11.5314L133.824 17.4136C133.837 17.4658 133.798 17.5179 133.747 17.5179H130.599C130.548 17.5179 130.509 17.4658 130.522 17.4136Z"
				fill="white"
			/>
			<path
				d="M66.8604 21.8352V17.1659C66.8604 17.1269 66.8991 17.0877 66.9378 17.0877H72.6771C72.7158 17.0877 72.7546 17.0486 72.7546 17.0094V15.1313C72.7546 15.0922 72.7158 15.0531 72.6771 15.0531H66.9378C66.8991 15.0531 66.8604 15.0139 66.8604 14.9748V10.9838C66.8604 10.9446 66.8991 10.9055 66.9378 10.9055H73.4639C73.5026 10.9055 73.5414 10.8663 73.5414 10.8273V8.94911C73.5414 8.90998 73.5026 8.87085 73.4639 8.87085H66.8604H64.6292C64.5905 8.87085 64.5518 8.90998 64.5518 8.94911V10.9055V15.0661V17.1008V21.9265V23.9872C64.5518 24.0264 64.5905 24.0656 64.6292 24.0656H66.8604H73.7347C73.7735 24.0656 73.8122 24.0264 73.8122 23.9872V22.0048C73.8122 21.9657 73.7735 21.9265 73.7347 21.9265H66.9249C66.8862 21.9135 66.8604 21.8874 66.8604 21.8352Z"
				fill="white"
			/>
			<path
				d="M169.976 23.9351L162.521 16.1486C162.495 16.1225 162.495 16.0704 162.521 16.0443L169.228 9.00128C169.28 8.94911 169.241 8.87085 169.177 8.87085H166.43C166.404 8.87085 166.391 8.88389 166.378 8.89694L160.69 14.8705C160.639 14.9226 160.561 14.8835 160.561 14.8183V8.94911C160.561 8.90998 160.522 8.87085 160.484 8.87085H158.329C158.291 8.87085 158.252 8.90998 158.252 8.94911V24.0003C158.252 24.0395 158.291 24.0786 158.329 24.0786H160.484C160.522 24.0786 160.561 24.0395 160.561 24.0003V17.3747C160.561 17.3095 160.652 17.2703 160.69 17.3225L167.138 24.0525C167.151 24.0656 167.177 24.0786 167.19 24.0786H169.937C169.989 24.0656 170.027 23.9743 169.976 23.9351Z"
				fill="white"
			/>
			<path
				d="M32.8499 0.75824L19.8105 10.5402L22.2353 4.77538L32.8499 0.75824Z"
				fill="#E17726"
				stroke="#E17726"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2.77295 0.75824L15.6962 10.6315L13.3876 4.77537L2.77295 0.75824Z"
				fill="#E27625"
				stroke="#E27625"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M28.1549 23.4393L24.6855 28.8129L32.1145 30.8867L34.2426 23.5567L28.1549 23.4393Z"
				fill="#E27625"
				stroke="#E27625"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M1.39258 23.5567L3.50775 30.8867L10.9238 28.8129L7.46728 23.4393L1.39258 23.5567Z"
				fill="#E27625"
				stroke="#E27625"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M10.524 14.3617L8.46045 17.518L15.812 17.8572L15.567 9.83594L10.524 14.3617Z"
				fill="#E27625"
				stroke="#E27625"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M25.0984 14.3618L19.9782 9.74475L19.8105 17.8573L27.162 17.5182L25.0984 14.3618Z"
				fill="#E27625"
				stroke="#E27625"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M10.9243 28.8129L15.3739 26.6348L11.5434 23.6089L10.9243 28.8129Z"
				fill="#E27625"
				stroke="#E27625"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20.249 26.6348L24.6857 28.8129L24.0795 23.6089L20.249 26.6348Z"
				fill="#E27625"
				stroke="#E27625"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M24.6857 28.8131L20.249 26.635L20.6101 29.5565L20.5714 30.7956L24.6857 28.8131Z"
				fill="#D5BFB2"
				stroke="#D5BFB2"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M10.9243 28.8131L15.0515 30.7956L15.0257 29.5565L15.3739 26.635L10.9243 28.8131Z"
				fill="#D5BFB2"
				stroke="#D5BFB2"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M15.1286 21.6786L11.4399 20.5831L14.0452 19.3701L15.1286 21.6786Z"
				fill="#233447"
				stroke="#233447"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20.4941 21.6786L21.5776 19.3701L24.1957 20.5831L20.4941 21.6786Z"
				fill="#233447"
				stroke="#233447"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M10.9238 28.8129L11.5687 23.4393L7.46729 23.5567L10.9238 28.8129Z"
				fill="#CC6228"
				stroke="#CC6228"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M24.0537 23.4393L24.6857 28.8129L28.1551 23.5567L24.0537 23.4393Z"
				fill="#CC6228"
				stroke="#CC6228"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M27.162 17.5179L19.8105 17.8571L20.4941 21.6786L21.5775 19.37L24.1957 20.583L27.162 17.5179Z"
				fill="#CC6228"
				stroke="#CC6228"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M11.4398 20.583L14.045 19.37L15.1285 21.6786L15.812 17.8571L8.46045 17.5179L11.4398 20.583Z"
				fill="#CC6228"
				stroke="#CC6228"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M8.46094 17.5179L11.5435 23.6089L11.4402 20.583L8.46094 17.5179Z"
				fill="#E27525"
				stroke="#E27525"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M24.1956 20.583L24.0796 23.6089L27.1621 17.5179L24.1956 20.583Z"
				fill="#E27525"
				stroke="#E27525"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M15.8125 17.8571L15.1289 21.6786L15.993 26.1913L16.1865 20.2439L15.8125 17.8571Z"
				fill="#E27525"
				stroke="#E27525"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M19.8104 17.8571L19.4492 20.2309L19.6298 26.1913L20.4939 21.6786L19.8104 17.8571Z"
				fill="#E27525"
				stroke="#E27525"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20.494 21.6788L19.6299 26.1914L20.249 26.6349L24.0796 23.609L24.1956 20.5831L20.494 21.6788Z"
				fill="#F5841F"
				stroke="#F5841F"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M11.4399 20.5831L11.5432 23.609L15.3737 26.6349L15.9928 26.1914L15.1286 21.6788L11.4399 20.5831Z"
				fill="#F5841F"
				stroke="#F5841F"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20.5716 30.7955L20.6103 29.5564L20.275 29.2695H15.3482L15.0257 29.5564L15.0515 30.7955L10.9243 28.813L12.3688 30.0129L15.2966 32.0606H20.3136L23.2542 30.0129L24.6858 28.813L20.5716 30.7955Z"
				fill="#C0AC9D"
				stroke="#C0AC9D"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20.2488 26.6348L19.6297 26.1914H15.9927L15.3737 26.6348L15.0254 29.5564L15.3478 29.2694H20.2747L20.61 29.5564L20.2488 26.6348Z"
				fill="#161616"
				stroke="#161616"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M33.4044 11.1793L34.5007 5.77966L32.8498 0.75824L20.249 10.2142L25.0984 14.3617L31.9469 16.3834L33.456 14.5965L32.7983 14.1139L33.8429 13.1488L33.0433 12.5227L34.088 11.7141L33.4044 11.1793Z"
				fill="#763E1A"
				stroke="#763E1A"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M1.12207 5.77966L2.23125 11.1793L1.5219 11.7141L2.57948 12.5227L1.77984 13.1488L2.82453 14.1139L2.16676 14.5965L3.67577 16.3834L10.5244 14.3617L15.3737 10.2142L2.77295 0.75824L1.12207 5.77966Z"
				fill="#763E1A"
				stroke="#763E1A"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M31.947 16.3833L25.0985 14.3617L27.1621 17.518L24.0796 23.6089L28.1552 23.5568H34.2428L31.947 16.3833Z"
				fill="#F5841F"
				stroke="#F5841F"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M10.5239 14.3617L3.67542 16.3833L1.39258 23.5568H7.46728L11.5428 23.6089L8.46038 17.518L10.5239 14.3617Z"
				fill="#F5841F"
				stroke="#F5841F"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M19.8101 17.8572L20.2487 10.2141L22.2348 4.77539H13.3872L15.3734 10.2141L15.8119 17.8572L15.9796 20.257L15.9925 26.1914H19.6296L19.6425 20.257L19.8101 17.8572Z"
				fill="#F5841F"
				stroke="#F5841F"
				strokeWidth="0.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

export function WalletConnectLogo({className}: {className?: string}) {
	return (
		<svg
			className={className}
			width="295"
			height="32"
			viewBox="0 0 295 32"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M286.586 9.49448H291.6V13.6306H295V17.3147H291.6V25.9046C291.6 27.2777 292.287 27.9311 293.764 27.9311C294.21 27.9311 294.674 27.8975 294.983 27.847V31.4475C294.468 31.5647 293.609 31.6483 292.596 31.6483C288.268 31.6483 286.586 30.2416 286.586 26.742V17.3147H283.993V13.6306H286.586V9.49448ZM282.808 20.3122H278.154C277.863 18.4197 276.575 17.0802 274.514 17.0802C272.041 17.0802 270.496 19.1231 270.496 22.589C270.496 26.1222 272.041 28.1319 274.531 28.1319C276.54 28.1319 277.845 26.9596 278.154 25.0004H282.825C282.567 29.237 279.322 31.9664 274.48 31.9664C268.933 31.9664 265.413 28.45 265.413 22.589C265.413 16.829 268.933 13.2453 274.445 13.2453C279.425 13.2453 282.585 16.2428 282.808 20.3122V20.3122ZM258.75 26.0891H263.352C262.803 29.6055 259.505 31.9664 254.955 31.9664C249.288 31.9664 245.888 28.4332 245.888 22.6899C245.888 16.963 249.323 13.2453 254.766 13.2453C260.123 13.2453 263.506 16.7785 263.506 22.2373V23.7277H250.816V24.0294C250.816 26.5916 252.482 28.3496 255.041 28.3496C256.878 28.3496 258.286 27.4617 258.75 26.0891V26.0891ZM254.817 16.8621C252.62 16.8621 251.04 18.487 250.868 20.7307H258.681C258.578 18.4365 257.05 16.8621 254.817 16.8621ZM226.294 31.5815V13.6306H231.137V16.829H231.24C232.219 14.6016 234.16 13.2958 236.993 13.2958C241.08 13.2958 243.381 15.808 243.381 20.011V31.5815H238.367V21.0324C238.367 18.6878 237.233 17.3483 234.984 17.3483C232.734 17.3483 231.309 18.9554 231.309 21.2832V31.5815H226.294ZM206.031 31.5815V13.6306H210.874V16.829H210.977C211.956 14.6016 213.897 13.2958 216.73 13.2958C220.817 13.2958 223.118 15.808 223.118 20.011V31.5815H218.103V21.0324C218.103 18.6878 216.97 17.3483 214.721 17.3483C212.471 17.3483 211.046 18.9554 211.046 21.2832V31.5815H206.031ZM194.338 28.2319C196.793 28.2319 198.356 26.2068 198.356 22.6063C198.356 19.0395 196.776 16.9798 194.338 16.9798C191.899 16.9798 190.302 19.0395 190.302 22.6063C190.302 26.2068 191.865 28.2319 194.338 28.2319ZM194.338 31.9664C188.774 31.9664 185.219 28.4836 185.219 22.589C185.219 16.7785 188.825 13.2453 194.338 13.2453C199.849 13.2453 203.456 16.7617 203.456 22.589C203.456 28.5004 199.901 31.9664 194.338 31.9664ZM172.22 32C165.007 32 160.491 27.2945 160.491 19.4916C160.491 11.705 165.042 7 172.22 7C178.179 7 182.712 10.6836 183.107 16.092H178.058C177.578 13.1281 175.242 11.1861 172.22 11.1861C168.305 11.1861 165.78 14.384 165.78 19.4748C165.78 24.6324 168.27 27.8139 172.237 27.8139C175.31 27.8139 177.526 26.0554 178.076 23.192H183.124C182.54 28.584 178.299 32 172.22 32V32ZM149.673 9.49448H154.687V13.6306H158.087V17.3147H154.687V25.9046C154.687 27.2777 155.374 27.9311 156.851 27.9311C157.297 27.9311 157.761 27.8975 158.07 27.847V31.4475C157.555 31.5647 156.696 31.6483 155.683 31.6483C151.355 31.6483 149.673 30.2416 149.673 26.742V17.3147H147.08V13.6306H149.673V9.49448ZM141.001 26.0891H145.603C145.054 29.6055 141.757 31.9664 137.206 31.9664C131.54 31.9664 128.139 28.4332 128.139 22.6899C128.139 16.963 131.574 13.2453 137.017 13.2453C142.375 13.2453 145.758 16.7785 145.758 22.2373V23.7277H133.067V24.0294C133.067 26.5916 134.733 28.3496 137.291 28.3496C139.13 28.3496 140.537 27.4617 141.001 26.0891V26.0891ZM137.069 16.8621C134.87 16.8621 133.291 18.487 133.119 20.7307H140.932C140.829 18.4365 139.3 16.8621 137.069 16.8621ZM120.412 7.41799H125.426V31.5811H120.412V7.41799ZM111.894 7.41799H116.908V31.5811H111.894V7.41799ZM97.9849 31.866C94.4819 31.866 91.8198 29.6554 91.8198 26.4239C91.8198 23.1584 94.3956 21.2664 98.9812 20.9983L103.617 20.7307V19.5416C103.617 17.8672 102.415 16.8957 100.526 16.8957C98.6545 16.8957 97.4696 17.8004 97.2291 19.1231H92.6446C92.8333 15.6403 95.8727 13.2453 100.749 13.2453C105.472 13.2453 108.546 15.6235 108.546 19.2403V31.5815H103.703V28.8348H103.6C102.57 30.7441 100.286 31.866 97.9849 31.866V31.866ZM99.6335 28.3328C101.814 28.3328 103.617 26.9428 103.617 25.0004V23.695L99.7365 23.929C97.8647 24.0626 96.8 24.8832 96.8 26.1559C96.8 27.4958 97.9332 28.3328 99.6335 28.3328ZM76.159 15.0705L71.4537 31.5815H66.5603L60 7.41799H65.3753L69.273 24.9173H69.4105L74.0473 7.41799H78.4259L83.0621 24.9173H83.1996L87.1151 7.41799H92.4899L85.9129 31.5815H81.0189L76.3137 15.0705H76.159Z"
				fill="#3999FA"
			/>
			<path
				d="M47.4661 12.9929L50.5023 15.9657C50.9168 16.3713 50.9168 17.029 50.5023 17.4347L36.8114 30.8395C36.3972 31.2452 35.7255 31.2452 35.311 30.8395L25.5941 21.3257C25.4905 21.2243 25.3225 21.2243 25.2189 21.3257L15.5023 30.8395C15.0881 31.2452 14.4161 31.2452 14.0019 30.8395L0.310646 17.4344C-0.103549 17.029 -0.103549 16.3713 0.310646 15.9654L3.34715 12.9929C3.76107 12.587 4.43308 12.587 4.84727 12.9929L14.5645 22.5067C14.6681 22.6079 14.836 22.6079 14.9396 22.5067L24.656 12.9929C25.0705 12.587 25.7422 12.587 26.1564 12.9929L35.8736 22.5067C35.9772 22.6079 36.1452 22.6079 36.2488 22.5067L45.9657 12.9929C46.3799 12.5872 47.0516 12.5872 47.4661 12.9929ZM10.4022 6.08492C18.6888 -2.02829 32.1242 -2.02829 40.4108 6.08492L41.4079 7.06145C41.8221 7.46712 41.8221 8.12483 41.4079 8.5305L37.9963 11.8707C37.7893 12.0736 37.4532 12.0736 37.2462 11.8707L35.8739 10.527C30.0928 4.86707 20.7199 4.86707 14.9394 10.527L13.4695 11.9661C13.2623 12.1686 12.9267 12.1686 12.7194 11.9661L9.30776 8.62587C8.89356 8.2202 8.89356 7.56249 9.30776 7.15682L10.4022 6.08492Z"
				fill="#3999FA"
			/>
		</svg>
	)
}

export function CoinbaseLogo({className}: {className?: string}) {
	return (
		<svg
			className={className}
			width="171"
			height="32"
			viewBox="0 0 171 32"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M34.5123 9.11467C28.3003 9.11467 23.4465 13.8304 23.4465 20.1432C23.4465 26.4562 28.1776 31.1315 34.5123 31.1315C40.847 31.1315 45.6604 26.3755 45.6604 20.1029C45.6604 13.8707 40.9293 9.11467 34.5123 9.11467ZM34.5542 26.5819C31.0167 26.5819 28.4244 23.8345 28.4244 20.1448C28.4244 16.4133 30.9748 13.6674 34.5123 13.6674C38.0918 13.6674 40.6825 16.4552 40.6825 20.1448C40.6825 23.8345 38.0918 26.5819 34.5542 26.5819ZM47.0171 13.9126H50.1027V30.7233H55.0388V9.52447H47.0171V13.9126ZM11.0239 13.6658C13.6162 13.6658 15.6729 15.2646 16.4537 17.6426H21.6785C20.7316 12.5591 16.5359 9.11467 11.0659 9.11467C4.85382 9.11467 0 13.8304 0 20.1448C0 26.4593 4.7312 31.1331 11.0659 31.1331C16.4133 31.1331 20.6913 27.6887 21.6381 22.5632H16.4537C15.7132 24.9412 13.6565 26.5819 11.0643 26.5819C7.48486 26.5819 4.97643 23.8345 4.97643 20.1448C4.97801 16.4133 7.44606 13.6658 11.0239 13.6658ZM140.806 18.0943L137.186 17.5619C135.459 17.3167 134.225 16.7423 134.225 15.3888C134.225 13.9126 135.83 13.1753 138.009 13.1753C140.395 13.1753 141.917 14.1998 142.246 15.8808H147.018C146.483 11.6169 143.192 9.1162 138.133 9.1162C132.908 9.1162 129.453 11.7814 129.453 15.5533C129.453 19.1607 131.716 21.2531 136.281 21.9082L139.901 22.4406C141.671 22.6858 142.658 23.3843 142.658 24.6959C142.658 26.377 140.93 27.074 138.544 27.074C135.623 27.074 133.978 25.885 133.731 24.0813H128.877C129.33 28.2226 132.579 31.1331 138.502 31.1331C143.892 31.1331 147.47 28.6728 147.47 24.4491C147.47 20.6772 144.879 18.709 140.806 18.0943ZM52.5708 0.791595C50.7609 0.791595 49.403 2.10323 49.403 3.90692C49.403 5.71062 50.7595 7.02225 52.5708 7.02225C54.3806 7.02225 55.739 5.71062 55.739 3.90692C55.739 2.10323 54.3806 0.791595 52.5708 0.791595ZM125.011 16.7827C125.011 12.1912 122.214 9.1162 116.29 9.1162C110.696 9.1162 107.57 11.9459 106.952 16.2922H111.847C112.094 14.6111 113.411 13.2172 116.208 13.2172C118.718 13.2172 119.952 14.3239 119.952 15.6775C119.952 17.4409 117.688 17.891 114.892 18.1782C111.107 18.5879 106.418 19.8995 106.418 24.8201C106.418 28.634 109.257 31.0927 113.782 31.0927C117.319 31.0927 119.54 29.6165 120.652 27.2789C120.817 29.3697 122.38 30.7233 124.56 30.7233H127.44V26.3367H125.012V16.7827H125.011ZM120.157 22.1131C120.157 24.9428 117.688 27.0336 114.685 27.0336C112.834 27.0336 111.27 26.2544 111.27 24.6152C111.27 22.5244 113.78 21.9501 116.084 21.7048C118.305 21.4999 119.539 21.0079 120.157 20.0641V22.1131ZM93.9535 9.11467C91.1967 9.11467 88.8933 10.2633 87.2481 12.1896V0.5867H82.312V30.7233H87.1655V27.9355C88.8107 29.9441 91.1566 31.1331 93.9535 31.1331C99.8766 31.1331 104.361 26.4593 104.361 20.1448C104.361 13.8304 99.7945 9.11467 93.9535 9.11467ZM93.2132 26.5819C89.6755 26.5819 87.0834 23.8345 87.0834 20.1448C87.0834 16.4552 89.7161 13.6674 93.2533 13.6674C96.8329 13.6674 99.3411 16.4148 99.3411 20.1448C99.3411 23.8345 96.7504 26.5819 93.2132 26.5819ZM70.5053 9.11467C67.297 9.11467 65.1984 10.4263 63.9646 12.2719V9.52447H59.0686V30.7217H64.0047V19.2011C64.0047 15.9615 66.0613 13.6658 69.1054 13.6658C71.9443 13.6658 73.7122 15.6744 73.7122 18.5864V30.7233H78.6483V18.2185C78.6502 12.8866 75.8948 9.11467 70.5053 9.11467ZM171 19.4479C171 13.3802 166.557 9.1162 160.592 9.1162C154.258 9.1162 149.609 13.8723 149.609 20.1448C149.609 26.7465 154.587 31.1331 160.675 31.1331C165.817 31.1331 169.848 28.0985 170.876 23.7941H165.733C164.993 25.6801 163.183 26.7465 160.756 26.7465C157.587 26.7465 155.201 24.7782 154.667 21.3338H170.999V19.4479H171ZM154.956 17.8072C155.739 14.8548 157.96 13.4206 160.51 13.4206C163.307 13.4206 165.446 15.0193 165.94 17.8072H154.956Z"
				fill="#0052FF"
			/>
		</svg>
	)
}

export function InfoIcon({className}: {className?: string}) {
	return (
		<svg
			className={className}
			xmlns="http://www.w3.org/2000/svg"
			width="20"
			height="20"
			viewBox="0 0 20 20"
			fill="currentColor"
		>
			<path
				fill-rule="evenodd"
				d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
				clip-rule="evenodd"
			/>
		</svg>
	)
}
